import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { signInWithGoogle } from "@/utils/auth";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="Privacy Policy - Seoro"
        description="Read Seoro's Privacy Policy to understand how we collect, use, and protect your personal data. Learn about our data practices, cookies, and your privacy rights."
        keywords="privacy policy, data protection, GDPR, CCPA, cookie policy, data privacy, Seoro privacy"
      />
      <Header onSignIn={handleSignIn} />
      
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="space-y-8">
          <div className="text-center space-y-4">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground">
              Last updated: January 2025
            </p>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              At Seoro, we are committed to protecting your privacy and ensuring the security of your personal data. 
              This Privacy Policy explains how we collect, use, disclose, and protect the information you provide while using our Service.
            </p>
          </div>

          <div className="prose prose-lg max-w-none space-y-8 text-muted-foreground">
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">1. Information We Collect</h2>
              <div className="space-y-3 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">1.1 Personal Data</h3>
                  <p>We collect information provided directly by you when you:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                    <li><strong>Account Creation</strong>: Name, email address, and profile information when you create an account using Google OAuth or other authentication methods</li>
                    <li><strong>Payment Information</strong>: Billing details, payment method information, and transaction history. Payment information is securely processed by third-party payment providers (including Stripe, Polar, or other providers) and is not stored on our servers</li>
                    <li><strong>Contact Information</strong>: Information you provide when contacting our support team or submitting inquiries</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">1.2 Service Usage Data</h3>
                  <p>We collect data related to your use of our Service, including:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                    <li>Website URLs, content, and metadata you provide for analysis and content generation</li>
                    <li>Writing style preferences, target audience information, brand voice, language preferences, and content generation settings</li>
                    <li>Articles, images, and other content created through our AI-powered systems</li>
                    <li>Credentials and connection information for third-party CMS platforms (WordPress, Shopify, Webflow) that you choose to connect</li>
                    <li>Search performance metrics, clicks, impressions, and ranking data when you connect your Google Search Console account</li>
                    <li>Keywords, anchor text, and backlink placement preferences you provide</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">1.3 Technical Data</h3>
                  <p>We automatically collect certain technical information, including device type, operating system, browser type, IP address, usage analytics, and log data.</p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">2. How We Use Your Information</h2>
              <div className="space-y-3 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">2.1 Service Delivery</h3>
                  <p>To provide and manage our services, including AI-powered content generation, backlink placement, website analysis, subscription management, and third-party integrations.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">2.2 Service Improvement</h3>
                  <p>To analyze platform usage patterns, refine AI models, develop new features, and troubleshoot technical issues.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">2.3 Communication</h3>
                  <p>To send service-related notifications, respond to inquiries, send promotional materials (you can opt out), and notify you of changes to our Service.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">2.4 Legal Compliance</h3>
                  <p>To comply with legal obligations, protect against fraudulent activities, and enforce our Terms of Service.</p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">3. Data Sharing and Disclosure</h2>
              <div className="space-y-3 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">3.1 Service Providers</h3>
                  <p>We share data with payment processors (Stripe, Polar), cloud infrastructure (Supabase), AI service providers (Google Gemini, OpenAI), backlink service providers, and analytics providers (with your consent).</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">3.2 Third-Party Integrations</h3>
                  <p>When you connect third-party services (CMS platforms, Google Search Console), they receive relevant data and authorization as requested by you.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">3.3 Legal Requirements</h3>
                  <p>We may disclose information if required by law, court orders, government investigations, or to protect our rights and safety.</p>
                </div>
                <p className="mt-3 font-semibold text-foreground">We do not sell your personal data to third parties.</p>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">4. Data Storage and Security</h2>
              <div className="space-y-3 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">4.1 Data Storage</h3>
                  <p>Your data is stored securely using Supabase with enterprise-grade security and encryption. Payment information is processed by payment providers and is not stored on our servers.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">4.2 Security Measures</h3>
                  <p>We implement encryption (TLS/SSL), strict access controls, regular security audits, and secure OAuth-based authentication.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">4.3 Data Breach Notification</h3>
                  <p>In the event of a data breach affecting your personal information, we will notify affected users promptly in compliance with applicable laws.</p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">5. Data Retention</h2>
              <p className="leading-relaxed">
                We retain your personal data for as long as necessary to provide our services and comply with legal obligations. 
                Active account data is retained while your account is active. Upon account deletion, we delete or anonymize your 
                personal data within 30 days, except where required for legal compliance (up to 10 years). Generated content 
                may be retained in anonymized form for Service improvement purposes.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">6. Cookies and Tracking Technologies</h2>
              <div className="space-y-3 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">6.1 Types of Cookies We Use</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>Essential Cookies</strong>: Required for the Service to function properly (authentication, session management)</li>
                    <li><strong>Analytics Cookies</strong>: Used to analyze Service usage and improve performance (only with your consent)</li>
                    <li><strong>Preference Cookies</strong>: Store your preferences and settings</li>
                    <li><strong>Marketing Cookies</strong>: Used for promotional campaigns and marketing (only with your consent)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">6.2 Cookie Management</h3>
                  <p>You can manage your cookie preferences through our cookie banner or your browser settings. However, disabling essential cookies may affect Service functionality.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">6.3 Third-Party Cookies</h3>
                  <p>Third-party services we use (payment processors, analytics providers, AI services) may set their own cookies. We do not control these cookies, and you should review their privacy policies.</p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">7. Your Rights and Choices</h2>
              <div className="space-y-3 leading-relaxed">
                <p>You have the following rights regarding your personal data:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Access and Portability</strong>: Request access to your personal data and export a copy in a portable format</li>
                  <li><strong>Correction and Deletion</strong>: Update or correct inaccurate information through your account settings, or request deletion by deleting your account or contacting support</li>
                  <li><strong>Objection and Restriction</strong>: Object to certain types of data processing, including marketing communications, or request restriction of processing</li>
                  <li><strong>Opt-Out Options</strong>: Unsubscribe from marketing communications, manage cookie preferences, and opt out of analytics tracking</li>
                </ul>
                <p className="mt-3">To exercise any of these rights, please contact us at <a href="mailto:support@seoro.so" className="text-primary hover:underline">support@seoro.so</a>. We will respond within 30 days in accordance with applicable laws.</p>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">8. International Data Transfers</h2>
              <p className="leading-relaxed">
                Your data may be transferred to and processed in countries other than your country of residence. 
                We ensure appropriate safeguards are in place, including standard contractual clauses, compliance 
                with GDPR, CCPA, and other applicable regulations, and adequate security measures regardless of data location.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">9. Children's Privacy</h2>
              <p className="leading-relaxed">
                Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal 
                information from children under 18. If we become aware that we have collected information from a child 
                under 18, we will promptly delete such information. Parents or guardians who believe their child has 
                provided personal information should contact us immediately.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">10. Third-Party Links and Services</h2>
              <p className="leading-relaxed">
                Our Service may contain links to third-party websites or integrate with third-party services. We are not 
                responsible for the privacy practices of these third parties. We encourage you to review the privacy policies 
                of any external sites or services you access through our Service, including CMS platforms (WordPress, Shopify, 
                Webflow), Google services (OAuth, Search Console), payment processors, and backlink service providers.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">11. AI-Generated Content and Data Processing</h2>
              <div className="space-y-3 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">11.1 AI Content Generation</h3>
                  <p>Your content inputs (website URLs, briefs, keywords) are processed by AI models (including Google Gemini and other providers) to generate content. AI-generated content is stored on our servers and associated with your account. We may use anonymized content data to improve our AI models and Service quality.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">11.2 Content Ownership</h3>
                  <p>You retain ownership of content generated for your account. We have a license to use your content inputs and generated content to provide and improve our Service. See our Terms of Service for detailed information about content ownership and licensing.</p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">12. Changes to This Privacy Policy</h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal 
                requirements, or other factors. When we make material changes, we will post the updated Privacy Policy on 
                this page, update the "Last Updated" date, and notify you via email or through the Service if changes are 
                significant. Your continued use of the Service after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">13. Contact Us</h2>
              <p className="leading-relaxed">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <p className="leading-relaxed">
                <strong>Email</strong>: <a href="mailto:support@seoro.so" className="text-primary hover:underline">support@seoro.so</a><br />
                <strong>Support</strong>: Available through your dashboard or our website
              </p>
              <p className="leading-relaxed mt-2">
                We will respond to your inquiries within 30 days in accordance with applicable data protection laws.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">14. Governing Law</h2>
              <p className="leading-relaxed">
                This Privacy Policy is governed by applicable data protection laws, including but not limited to the General 
                Data Protection Regulation (GDPR) for users in the European Economic Area, the California Consumer Privacy 
                Act (CCPA) for California residents, and other applicable local data protection laws. Any disputes arising 
                from this Privacy Policy will be resolved in accordance with our Terms of Service and applicable law.
              </p>
            </section>

            <div className="mt-8 p-6 bg-muted/30 rounded-lg border border-border/50">
              <p className="text-foreground font-semibold">
                By using Seoro, you acknowledge that you have read, understood, and agree to this Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer onSignIn={handleSignIn} />
    </div>
  );
};

export default PrivacyPolicy;


